import { Toast } from '@getvim/atomic-ui';
import formatForClipboard from '../../../utils/clipboardFormatter';
import { useClipboard } from './useClipboard';
import { GetOrderAssistResult, OrderAssistOption } from '../../../types';
import { useFlowState } from '@getvim/flow-context-provider';
import { SearchRequiredState } from '../../SourceConfigWrapper';

function useCopyResultsToClipboard(): (
  results: GetOrderAssistResult,
  displayPreferredProvidersList: boolean,
  preferredProviders: OrderAssistOption[],
) => Promise<{
  success: boolean;
}> {
  const { sourceConfig } = useFlowState<SearchRequiredState>();
  const {
    supportScoreRatingBadges,
    supportOpeningHours,
    useGenericSearchTitles,
    supportNetworkStatusBadge,
    supportAcceptingNewPatientsInCard,
    displayDistanceInExportDetails,
  } = sourceConfig;

  const copy = useClipboard();
  const { createToast, ToastTypes } = Toast;

  const enum CopyResultsText {
    SuccessTitle = 'All done!',
    SuccessMessage = 'The details were copied to the clipboard, you can now paste them to any text field',
  }

  const getDetailsFromResults = (options: OrderAssistOption[]): string => {
    const text = options
      .map((item) => {
        const formattedResult = formatForClipboard({
          option: item,
          supportScoreRatingBadges,
          supportNetworkStatusBadge,
          supportOpeningHours,
          supportAcceptingNewPatientsInCard,
          displayDistanceInExportDetails,
        });
        return `\n\n${formattedResult}`;
      })
      .join(',');
    return text;
  };

  const copyResultsToClipboard = async (
    results: GetOrderAssistResult,
    displayPreferredProvidersList: boolean,
    preferredProviders: OrderAssistOption[],
  ) => {
    let text = `Order Assist\n\n`;

    if (preferredProviders?.length && displayPreferredProvidersList) {
      text += `In-network featured search results (${preferredProviders.length})`;
      text += `${getDetailsFromResults(preferredProviders)}\n\n`;
    }

    if (results.searchResults?.length) {
      const title = useGenericSearchTitles ? 'Search Results' : 'In-network search results';
      text += `${title} (${results.searchResults.length})`;
      text += `${getDetailsFromResults(results.searchResults)}\n\n`;
    }

    if (results.alternativeResults?.length) {
      text += `In-network alternative recommendations`;
      text += `${getDetailsFromResults(results.alternativeResults)}\n`;
    }

    const success = await copy(text);

    if (success) {
      createToast({
        title: CopyResultsText.SuccessTitle,
        message: CopyResultsText.SuccessMessage,
        type: ToastTypes.SUCCESS,
        html: true,
      });
    }

    return { success };
  };

  return copyResultsToClipboard;
}

export default useCopyResultsToClipboard;
